<template>
  <div>
    <div class="loader" v-if="isLoading"></div>
    <div class="loadertext" v-if="isLoading">
      {{ $t("loadingTxt") }}...
    </div>

    <div class="main-wrapper" :class="{ 'blur-content': isLoading }">
      <sidebar-component classMenu="TemplateSetting"></sidebar-component>

      <div class="page-wrapper">
        <navbar-component></navbar-component>

        <div class="page-content">
          <!-- Breadcrumb / Back -->
          <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
            <div>
              <button class="btn btn-link px-0 text-decoration-none mb-2" @click="goBack">
                <i class="mdi mdi-arrow-left me-1"></i>
                Back to Customer List
              </button>
              <h4 class="mb-0">File History</h4>
            </div>
          </div>

          <!-- Info Card -->
          <div class="card mb-3">
            <div class="card-body">
              <div class="row mb-3 mt-3 align-items-center">
                <div class="col-auto">
                  <strong>Customer Name :</strong> {{ companyName }}
                </div>
              </div>

              <!-- Input fields row 1 -->
              <div class="row mb-2">
                <div class="col-lg-4 col-md-4 mb-2">
                  <label class="form-label">Report Period</label>
                  <Datepicker
                    v-model="filters.report_period"
                    format="yyyy-MM-dd"
                    :bootstrap-styling="true"
                    placeholder="YYYY-MM-DD"
                    input-class="form-control bg-white"
                  ></Datepicker>
                </div>
                
                <div class="col-lg-4 col-md-4 mb-2">
                  <label class="form-label">No Invoice</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="filters.inv_no"
                    placeholder="No Invoice"
                  />
                </div>


                <div class="col-lg-4 col-md-4 mb-2">
                  <label class="form-label">No FP</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="filters.fp_no"
                    placeholder="No FP"
                  />
                </div>

               
              </div>

              <!-- Input fields row 2 -->
              <div class="row mb-2">
                 <div class="col-lg-4 col-md-4 mb-2">
                  <label class="form-label">Rate</label>
                  <input
                    type="number"
                    class="form-control"
                    v-model="filters.rate"
                    placeholder="Rate"
                  />
                </div>

                <div class="col-lg-4 col-md-4 mb-2">
                  <label class="form-label">Contract Due Date</label>
                  <Datepicker
                    v-model="filters.contract_period"
                    format="yyyy-MM-dd"
                    :bootstrap-styling="true"
                    placeholder="YYYY-MM-DD"
                    input-class="form-control bg-white"
                  ></Datepicker>
                </div>

                <div class="col-lg-4 col-md-4 mb-2 d-flex align-items-end">
                  <button class="btn btn-danger w-100" @click="generateReport" :disabled="isGenerating">
                    <span v-if="isGenerating">
                      <i class="mdi mdi-loading mdi-spin"></i>
                    </span>
                    <span v-else>Generate</span>
                  </button>
                </div>
              </div>

              <!-- Bulk Action Buttons -->
              <div class="d-flex justify-content-end gap-2 mb-2">
                <button
                  class="btn btn-warning text-dark fw-semibold"
                  :disabled="!canPosting"
                  @click="postingSelected"
                >
                  Posting
                </button>
                <button
                  class="btn btn-success"
                  :disabled="!canDownloadExcel"
                  @click="downloadExcel"
                >
                  <i class="mdi mdi-microsoft-excel me-1"></i> Download Excel
                </button>
              </div>

              <!-- Table -->
              <vue-good-table
                mode="remote"
                ref="historyTable"
                :columns="columns"
                :rows="rows"
                :totalRows="totalRecords"
                :isLoading.sync="isLoading"
                :pagination-options="{
                  enabled: true,
                  perPage: serverParams.per_page,
                  perPageDropdown: [],
                }"
                :select-options="{
                  enabled: true,
                  selectOnCheckboxOnly: true,
                  selectionText: $t('data_selected'),
                  clearSelectionText: $t('clear'),
                }"
                styleClass="vgt-table striped bordered"
                @on-page-change="onPageChange"
                @on-selected-rows-change="onSelectionChanged"
              >
                <template slot="table-row" slot-scope="props">
                  <!-- Action column -->
                  <span v-if="props.column.field === 'actions'">
                    <div class="d-flex gap-1">
                      <!-- View — always visible -->
                      <button
                        type="button"
                        class="btn btn-icon btn-sm btn-outline-secondary"
                        @click="goToView(props.row)"
                        title="View"
                      >
                        <i class="mdi mdi-eye-outline"></i>
                      </button>
                      <!-- Edit — DRAFT & POSTED only (not PRINTED) -->
                      <button
                        v-if="canEditRow(props.row)"
                        type="button"
                        class="btn btn-icon btn-sm btn-outline-primary"
                        @click="goToEdit(props.row)"
                        title="Edit"
                      >
                        <i class="mdi mdi-square-edit-outline"></i>
                      </button>
                      <!-- Delete — DRAFT only -->
                      <button
                        v-if="canDeleteRow(props.row)"
                        type="button"
                        class="btn btn-icon btn-sm btn-outline-danger"
                        @click="deleteReport(props.row)"
                        title="Delete"
                      >
                        <i class="mdi mdi-trash-can-outline"></i>
                      </button>
                    </div>
                  </span>

                  <!-- Status badge -->
                  <span v-else-if="props.column.field === 'status'">
                    <span :class="statusClass(props.row.status)">
                      {{ props.row.status }}
                    </span>
                  </span>

                  <!-- Report period column -->
                  <span v-else-if="props.column.field === 'report_period'">
                    <div class="d-flex align-items-center gap-1">
                      {{ props.row.report_period }}
                      <i class="mdi mdi-calendar text-muted"></i>
                    </div>
                  </span>

                  <!-- Rate formatted -->
                  <span v-else-if="props.column.field === 'rate'">
                    {{ formatCurrency(props.row.rate) }}
                  </span>

                  <!-- Total Rate formatted -->
                  <span v-else-if="props.column.field === 'total_rate'">
                    {{ formatCurrency(props.row.total_rate) }}
                  </span>

                  <span v-else>
                    {{ props.formattedRow[props.column.field] || "-" }}
                  </span>
                </template>
              </vue-good-table>
            </div>
          </div>
        </div>

        <footer-component></footer-component>
      </div>
    </div>
  </div>
</template>

<script>
import sidebarComponent from "@/components/_partials/_sidebar";
import navbarComponent from "@/components/_partials/_navbar";
import footerComponent from "@/components/_partials/_footer";
import Datepicker from "vuejs-datepicker";

export default {
  name: "ProformaRentalHistory",
  components: {
    "sidebar-component": sidebarComponent,
    "navbar-component": navbarComponent,
    "footer-component": footerComponent,
    Datepicker,
  },
  data() {
    return {
      isLoading: false,
      isGenerating: false,
      totalRecords: 0,
      permision_role: {
        method_create: true,
        method_read: true,
        method_update: true,
        method_delete: true,
      },
      filters: {
        report_period: "",
        contract_period: "",
        inv_no: "",
        rate: "",
        fp_no: "",
      },
      serverParams: {
        page: 1,
        per_page: 10,
      },
      rows: [],
      selectedRows: [],
      // proforma_rental_company_id = route param (e.g. 3), used for GET detail/{id}
      // company_id = route query (e.g. 745), used for POST generate body
      company: {
        proforma_rental_company_id: this.$route.params.customerId,
        company_id: this.$route.query.company_id || null,
        company_name: this.$route.query.company_name || "-",
        company_code: this.$route.query.company_code || "-",
        sap_code: this.$route.query.sap_code || "-",
      },
    };
  },
  computed: {
    columns() {
      return [
        { label: "Action", field: "actions", sortable: false },
        { label: "Report name", field: "report_name" },
        {
          label: "Report Period",
          field: "report_period",
          filterOptions: {
            enabled: true,
            placeholder: "JAN 2026 -",
          },
        },
        { label: "No Invoice", field: "no_invoice" },
        { label: "Jumlah Unit", field: "jumlah_unit", type: "number" },
        { label: "Rate", field: "rate" },
        { label: "Total Rate", field: "total_rate" },
        {
          label: "Contract Period",
          field: "contract_period",
          filterOptions: {
            enabled: true,
            placeholder: "JAN 2026 -",
          },
        },
        {
          label: "Status",
          field: "status",
          filterOptions: {
            enabled: true,
            placeholder: "Status",
          },
        },
        {
          label: "Generate date",
          field: "generate_date",
          filterOptions: {
            enabled: true,
            placeholder: "JAN 2026 -",
          },
        },
        { label: "Generate by", field: "generate_by" },
        { label: "Update By", field: "update_by" },
        {
          label: "Update Date",
          field: "update_date",
          filterOptions: {
            enabled: true,
            placeholder: "JAN 2026 -",
          },
        },
      ];
    },
    proformaInvoiceEndpoint() {
      return String(this.$settings?.endPointProformaInvoice || "").replace(/\/?$/, "/");
    },
    companyName() {
      return this.company.company_name || "-";
    },
    // Posting: aktif jika semua selected berstatus DRAFT
    canPosting() {
      if (!this.selectedRows.length) return false;
      return this.selectedRows.every((r) => r.status === "DRAFT");
    },
    // Download Excel: aktif jika tepat 1 data di-checklist (tidak bisa multiple) dan status BUKAN DRAFT
    canDownloadExcel() {
      if (this.selectedRows.length !== 1) return false;
      const status = String(this.selectedRows[0].status || "").toUpperCase();
      return status !== "DRAFT";
    },
  },
  methods: {
    // ── Navigation ──────────────────────────────────────────────────────────
    goBack() {
      this.$router.push({ name: "ProformaRentalList" });
    },
    goToView(row) {
      this.$router.push({
        name: "ProformaRentalView",
        params: {
          customerId: this.company.proforma_rental_company_id,
          reportId: row.id,
        },
        query: {
          company_name: this.company.company_name,
          company_code: this.company.company_code,
          sap_code: this.company.sap_code,
          company_id: this.company.company_id,
          report_name: row.report_name,
          status: row.status,
        },
      });
    },
    goToEdit(row) {
      this.$router.push({
        name: "ProformaRentalEdit",
        params: {
          customerId: this.company.proforma_rental_company_id,
          reportId: row.id,
        },
        query: {
          company_name: this.company.company_name,
          company_code: this.company.company_code,
          sap_code: this.company.sap_code,
          company_id: this.company.company_id,
          report_name: row.report_name,
          status: row.status,
        },
      });
    },

    // ── Row permissions ──────────────────────────────────────────────────────
    canEditRow(row) {
      // DRAFT dan POSTED bisa di-edit, PRINTED tidak bisa
      return row.status !== "PRINTED" && this.permision_role.method_update;
    },
    canDeleteRow(row) {
      return row.status === "DRAFT" && this.permision_role.method_delete;
    },

    // ── Generate ─────────────────────────────────────────────────────────────
    async generateReport() {
      const result = await this.$swal({
        title: "Generate Report?",
        text: `Report baru akan dibuat untuk customer ${this.companyName}.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Ya, Generate",
        cancelButtonText: "Batal",
      });

      if (!result.value) return;

      // Format report_period ke "YYYY-MM-DD"
      const reportPeriodFormatted = this.filters.report_period
        ? this.$moment(this.filters.report_period).format("YYYY-MM-DD")
        : "";

      // Format contract_period ke "YYYY-MM-DD"
      const contractPeriodFormatted = this.filters.contract_period
        ? this.$moment(this.filters.contract_period).format("YYYY-MM-DD")
        : "";

      this.isGenerating = true;
      try {
        await this.$http.post(
          `${this.proformaInvoiceEndpoint}v1/proforma-rental-header/generate`,
          {
            company_id      : Number(this.company.company_id),
            inv_no          : this.filters.inv_no,
            fp_no           : this.filters.fp_no,
            report_period   : reportPeriodFormatted,
            rate            : Number(this.filters.rate) || 0,
            contract_period : contractPeriodFormatted,
          }
        );
        this.showSuccess("Generate berhasil. Status: DRAFT");
        await this.loadItems();
      } catch (error) {
        this.handleRequestError(error, "Gagal generate report.");
      } finally {
        this.isGenerating = false;
      }
    },

    // ── Posting ──────────────────────────────────────────────────────────────
    async postingSelected() {
      if (!this.canPosting) return;

      const result = await this.$swal({
        title: this.$t("areYouSure"),
        text: `${this.selectedRows.length} report akan di-posting.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Ya, Posting",
        cancelButtonText: "Batal",
      });

      if (!result.value) return;

      this.isLoading = true;
      try {
        const ids = this.selectedRows.map((r) => r.id);
        await this.$http.post(
          `${this.proformaInvoiceEndpoint}v1/proforma-rental-header/posting`,
          { proforma_rental_header_ids: ids }
        );
        this.showSuccess("Posting berhasil. Status: POSTED");
        this.selectedRows = [];
        await this.loadItems();
      } catch (error) {
        this.handleRequestError(error, "Gagal melakukan posting.");
      } finally {
        this.isLoading = false;
      }
    },

    // ── Download Excel ────────────────────────────────────────────────────────
    // Download per proforma_rental_header_id dari item yang di-checklist
    async downloadExcel() {
      if (!this.canDownloadExcel) return;

      const selectedRow = this.selectedRows[0];
      const headerId = selectedRow.id || selectedRow.raw?.proforma_rental_header_id;
      if (!headerId) return;

      this.$swal({
        icon: "warning",
        title: "Please Wait",
        text: "Don't Close This Page !!!",
        timerProgressBar: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showCancelButton: false,
        showConfirmButton: false,
        didOpen: async () => {
          this.$swal.showLoading();
          try {
            const response = await this.$http.get(
              `${this.proformaInvoiceEndpoint}v1/proforma-rental-header/download-excel/${headerId}`,
              { responseType: "blob" }
            );
            this.triggerFileDownload(
              response.data,
              this.getDownloadFilename(response, selectedRow.report_name || `proforma_rental_${this.company.company_name}`)
            );
            this.$swal.close();
            this.showSuccess("Download Excel berhasil.");
          } catch (error) {
            this.$swal.close();
            this.handleRequestError(error, "Gagal download Excel.");
          }
        },
      });
    },

    // ── Delete ────────────────────────────────────────────────────────────────
    async deleteReport(row) {
      const result = await this.$swal({
        title: this.$t("areYouSure"),
        text: `Report ${row.report_name} akan dihapus beserta semua detailnya.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Ya, hapus",
        cancelButtonText: "Batal",
      });

      if (!result.value) return;

      this.isLoading = true;
      try {
        await this.$http.delete(
          `${this.proformaInvoiceEndpoint}v1/proforma-rental-header/delete/${row.id}`
        );
        this.showSuccess("Report berhasil dihapus.");
        await this.loadItems();
      } catch (error) {
        this.handleRequestError(error, "Gagal menghapus report.");
      } finally {
        this.isLoading = false;
      }
    },

    // ── Table ─────────────────────────────────────────────────────────────────
    onPageChange(params) {
      this.serverParams.page = params.currentPage;
      this.loadItems();
    },
    onSelectionChanged(params) {
      this.selectedRows = params.selectedRows || [];
    },

    async loadItems() {
      this.isLoading = true;
      try {
        const response = await this.$http.get(
          `${this.proformaInvoiceEndpoint}v1/proforma-rental-header/detail/${this.company.proforma_rental_company_id}`
        );
        // Response shape: { proforma_rental_company_id, company_id, ..., header: [...] }
        const list = response.data?.header
          || response.data?.datas?.header
          || response.data?.data?.header
          || [];
        this.rows = list.map((item) => this.mapRow(item));
        this.totalRecords = list.length;
        this.selectedRows = [];
      } catch (error) {
        this.rows = [];
        this.totalRecords = 0;
        this.handleRequestError(error, "Gagal load file history.");
      } finally {
        this.isLoading = false;
      }
    },

    mapRow(item) {
      return {
        id          : item.proforma_rental_header_id || item.id,
        report_name : item.report_name || "-",
        report_period: this.formatReportPeriod(item.report_period),
        contract_period: (item.contract_period || item.contract_duedate) ? this.formatDate(item.contract_period || item.contract_duedate) : "-",
        no_invoice  : item.inv_no || item.no_invoice || "-",
        jumlah_unit : item.total_unit ?? item.jumlah_unit ?? 0,
        rate        : item.rate ?? 0,
        total_rate  : item.total_rate ?? item.rate ?? 0,
        status      : String(item.status || "DRAFT").toUpperCase(),
        generate_date: this.formatDate(item.generate_date || item.created_at),
        generate_by : item.created_by || item.generate_by || "-",
        update_by   : item.updated_by || item.update_by || "-",
        update_date : this.formatDate(item.updated_at || item.update_date),
        raw         : item,
      };
    },

    // ── Helpers ───────────────────────────────────────────────────────────────
    formatReportPeriod(value) {
      if (!value || value === "-") return "-";
      const MONTH_NAMES_ID = [
        "Januari", "Februari", "Maret", "April", "Mei", "Juni",
        "Juli", "Agustus", "September", "Oktober", "November", "Desember"
      ];
      const parsed = this.$moment(value, ["YYYY-MM-DD", "YYYY-MM", "YYYY-MM-DD HH:mm:ss", "MM-YYYY", "MM/YYYY", "YYYY/MM"], false);
      if (parsed.isValid()) {
        return `${MONTH_NAMES_ID[parsed.month()]} ${parsed.year()}`;
      }
      const str = String(value).trim();
      const textMatch = str.match(/^([a-zA-Z]+)\s+(\d{4})$/);
      if (textMatch) {
        const monthStr = textMatch[1].toLowerCase();
        const yearStr = textMatch[2];
        const EnglishToIndo = {
          january: "Januari", february: "Februari", march: "Maret", april: "April",
          may: "Mei", june: "Juni", july: "Juli", august: "Agustus",
          september: "September", october: "Oktober", november: "November", december: "Desember"
        };
        if (EnglishToIndo[monthStr]) {
          return `${EnglishToIndo[monthStr]} ${yearStr}`;
        }
        const idx = MONTH_NAMES_ID.findIndex((m) => m.toLowerCase() === monthStr);
        if (idx !== -1) {
          return `${MONTH_NAMES_ID[idx]} ${yearStr}`;
        }
      }
      return value;
    },
    statusClass(status) {
      return {
        badge: true,
        "bg-soft-warning text-warning fw-semibold": status === "DRAFT",
        "bg-soft-primary text-primary fw-semibold": status === "POSTED",
        "bg-soft-success text-success fw-semibold": status === "PRINTED",
      };
    },
    formatDate(value) {
      if (!value) return "-";
      const parsed = this.$moment(value);
      return parsed.isValid() ? parsed.format("DD-MMM-YYYY") : value;
    },
    formatCurrency(value) {
      const num = Number(value || 0);
      if (!Number.isFinite(num)) return value || "-";
      return num.toLocaleString("id-ID", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    getDownloadFilename(response, reportName) {
      const disposition = response?.headers?.["content-disposition"] || "";
      const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      const asciiMatch = disposition.match(/filename="?([^"]+)"?/i);
      const extracted = utfMatch?.[1]
        ? decodeURIComponent(utfMatch[1])
        : asciiMatch?.[1];
      if (extracted) return extracted;
      return `proforma_rental_${reportName || "export"}_${this.$moment().format("DDMMYYYY")}.xlsx`;
    },
    triggerFileDownload(blobData, fileName) {
      const fileURL = window.URL.createObjectURL(new Blob([blobData]));
      const fileLink = document.createElement("a");
      fileLink.href = fileURL;
      fileLink.setAttribute("download", fileName);
      document.body.appendChild(fileLink);
      fileLink.click();
      document.body.removeChild(fileLink);
      window.URL.revokeObjectURL(fileURL);
    },
    handleRequestError(error, fallbackMessage) {
      if (error?.response?.status === 500) {
        this.$router.push("/server-error");
        return;
      }
      if (error?.response?.status === 404) {
        return;
      }
      this.showError(
        error?.response?.data?.errors?.message ||
        error?.response?.data?.message ||
        fallbackMessage
      );
    },
    showError(message) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      Toast.fire({ icon: "error", title: message });
    },
    showSuccess(message) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      Toast.fire({ icon: "success", title: message });
    },

    // ── Permission ────────────────────────────────────────────────────────────
    fetchIt() {
      const userDatas = this.$getUserInfo ? this.$getUserInfo() : null;
      if (!userDatas?.sub?.permision_role) {
        this.loadItems();
        return;
      }

      let rolePermision = null;
      userDatas.sub.permision_role.forEach((permision) => {
        if (
          permision.permission.controller === "ProformaInvoiceNstController" ||
          permision.permission.controller === "ProformaRentalController" ||
          permision.permission.controller === "ProformaRentalHeaderController"
        ) {
          rolePermision = permision;
        }
      });

      if (rolePermision && !rolePermision.method_read) {
        this.$router.push("/authorized-error");
        return;
      }
      if (rolePermision) this.permision_role = rolePermision;

      this.loadItems();
    },
  },
  mounted() {
    this.fetchIt();
  },
};
</script>

<style scoped>
.bg-soft-warning {
  background: rgba(255, 193, 7, 0.18);
}
.bg-soft-primary {
  background: rgba(13, 110, 253, 0.12);
}
.bg-soft-success {
  background: rgba(25, 135, 84, 0.15);
}
</style>
